import React, { useEffect, useState } from "react";
import StarRating from "./StarRating";

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  redirectUrl: string;
  rating: number;
  totalStars: number;
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  redirectUrl,
  rating,
  totalStars,
}) => {
  const [message, setMessage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [hover, setHover] = useState(0);

  const basePath = process.env.PUBLIC_URL;
  const starSize = "clamp(36px, 8vw, 60px)";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        onClose();
        window.location.href = redirectUrl;
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [submitted, onClose, redirectUrl]);

  if (!isOpen) return null;

  const handleEmailChange = (e: any) => {
    const value = e.target.value;
    setEmail(value);

    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-6 w-[90%] max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {!submitted ? (
          <>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              {Array.from({ length: totalStars }, (_, i) => i + 1).map(
                (star) => {
                  const isActive =
                    star <= rating || (hover >= star && star > rating);
                  return (
                    <span
                      key={star}
                      style={{
                        cursor: "pointer",
                        width: starSize,
                        height: starSize,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isActive ? (
                        <img
                          src={`${basePath}/images/selected-star.png`}
                          alt="selected star"
                          style={{
                            width: starSize,
                            height: starSize,
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            fontSize: starSize,
                            color: "#ccc",
                            lineHeight: 1,
                          }}
                        >
                          ★
                        </span>
                      )}
                    </span>
                  );
                },
              )}
            </div>
            <h2 className="text-lg font-semibold theme-text-color mb-4 text-center">
              We value your feedback
            </h2>
            <h3 className="text-lg mb-4 theme-text-color text-center">
              Thanks for telling what you liked and where you think we can
              improve
            </h3>

            <textarea
              className="w-full border rounded-md p-2 mb-3"
              placeholder="Your feedback"
              maxLength={600}
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <p className="text-xs text-right mb-3">{message.length}/600</p>

            <input
              className="w-full border rounded-md p-2 mb-3"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />

            <input
              className={`w-full border rounded-md p-2 mb-1 ${
                emailError ? "border-red-500" : "border-gray-300"
              }`}
              type="email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
            />

            {emailError && <p className="text-red-500 text-sm">{emailError}</p>}

            <button
              className="
    w-full py-2 rounded-md
    bg-green-600 text-white cursor-pointer
    disabled:bg-gray-400
    disabled:text-gray-700
    disabled:cursor-not-allowed
  "
              onClick={() => {
                setSubmitted(true);
              }}
              disabled={!message || !firstName || !email || emailError !== ""}
            >
              Submit
            </button>
          </>
        ) : (
          <div className="text-center py-8">
            <h2 className="text-lg font-semibold mb-2">
              Thank you for your feedback!
            </h2>
            <p className="text-sm text-gray-600">
              Redirecting you back to the app…
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
